import React, { useRef } from 'react';
import { Label, TextInput, Button, Card } from 'flowbite-react';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { TFunction } from 'i18next';
import { IMultiLanguageValue } from '../../../../common/interfaces/MultiLanguageValue.interface';
import { ILanguageOption } from '../../../../common/interfaces/LanguageOption.interface';
import { LanguageDropdown } from '../../LanguageSelectionDropdown/LanguageDropdown';
import { useAppSelector } from '../../../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../../../redux/selectors/displaySettingsSelectors';

/**
 * Generic item in a multi-language list
 * @property key - Unique identifier for the item
 * @property value - Array of language/value pairs for this item
 * @property [key: string] - Additional custom properties (e.g., isCorrect for quiz answers)
 */
export interface MultiLanguageListItem {
  key: string;
  value: IMultiLanguageValue[];
  [key: string]: any; // Allow additional properties like isCorrect
}

/**
 * Props for rendering each item
 */
export interface ItemRendererProps {
  item: MultiLanguageListItem;
  index: number;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  selectedLanguage: ILanguageOption;
  t: TFunction;
}

interface MultiLanguageListProps {
  items: MultiLanguageListItem[];
  onChange: (items: MultiLanguageListItem[]) => void;
  selectedLanguage: ILanguageOption;
  onLanguageChange: (lang: ILanguageOption) => void;
  minItems?: number;
  maxItems?: number;
  itemLabelKey?: string; // translation key for item label
  addButtonTextKey?: string; // translation key for add button
  languageDropdownLabelKey?: string; // translation key for language dropdown label
  placeholderKey?: string; // translation key for input placeholder
  required?: boolean;
  t: TFunction;
  renderExtraContent?: (
    item: MultiLanguageListItem,
    updateItem: (updates: Partial<MultiLanguageListItem>) => void
  ) => React.ReactNode;
  wrapInCard?: boolean;
}

/**
 * MultiLanguageList - Dynamic list of multi-language items with add/remove functionality
 *
 * **Component Type:** Stateless (Controlled)
 *
 * **Purpose:**
 * Manages dynamic lists where each item needs multi-language support and can have
 * additional custom properties (e.g., quiz answer options with isCorrect flag).
 *
 * **State Management:**
 * - No internal state - items managed by parent via useState
 * - Language selection controlled by parent (useMultiLanguageInput hook)
 * - Add/remove operations update parent state via onChange
 *
 * **Usage:**
 * ```tsx
 * const { selectedLanguage, setSelectedLanguage } = useMultiLanguageInput();
 * const [items, setItems] = useState<MultiLanguageListItem[]>([]);
 *
 * <MultiLanguageList
 *   items={items}
 *   onChange={setItems}
 *   selectedLanguage={selectedLanguage}
 *   onLanguageChange={setSelectedLanguage}
 *   minItems={2}
 *   maxItems={6}
 *   itemLabelKey="content.option"
 *   renderExtraContent={(item, updateItem) => (
 *     <Checkbox
 *       checked={item.isCorrect}
 *       onChange={() => updateItem({ isCorrect: !item.isCorrect })}
 *     />
 *   )}
 *   t={t}
 * />
 * ```
 *
 * **When to Use:**
 * - Quiz answer options (with isCorrect property)
 * - Dynamic questionnaire questions
 * - Any list where items can be added/removed and need multi-language support
 *
 * **Item Structure:**
 * Each item must have:
 * - `key`: Unique identifier (use UUID for new items)
 * - `value`: IMultiLanguageValue[] array
 * - Any custom properties (accessed via renderExtraContent)
 *
 * @param props - Component props including items array and onChange handler
 * @returns React component
 */
export const MultiLanguageList: React.FC<MultiLanguageListProps> = ({
  items,
  onChange,
  selectedLanguage,
  onLanguageChange,
  minItems = 1,
  maxItems = 10,
  itemLabelKey = 'content.item',
  addButtonTextKey = 'content.addItem',
  languageDropdownLabelKey = 'content.selectLanguage',
  placeholderKey = 'placeholders.item',
  required = false,
  t,
  renderExtraContent,
  wrapInCard = true,
}) => {
  const isDarkMode = useAppSelector(selectIsDarkMode);

  // Use a counter ref to guarantee unique keys even after remove+add operations
  const keyCounterRef = useRef(
    items.length > 0
      ? Math.max(...items.map((item) => parseInt(item.key, 10) || 0)) + 1
      : 1
  );

  const getFieldValue = (
    fieldData: IMultiLanguageValue[],
    languageCode: string
  ): string => {
    return (
      fieldData.find((item) => item.language === languageCode)?.value || ''
    );
  };

  const addItem = () => {
    const newItem: MultiLanguageListItem = {
      key: `${keyCounterRef.current}`,
      value: [] as IMultiLanguageValue[],
    };
    keyCounterRef.current += 1;
    onChange([...items, newItem]);
  };

  const removeItem = (keyToRemove: string) => {
    if (items.length > minItems) {
      onChange(items.filter((item) => item.key !== keyToRemove));
    }
  };

  const updateItemValue = (
    itemKey: string,
    languageCode: string,
    value: string
  ) => {
    onChange(
      items.map((item) => {
        if (item.key === itemKey) {
          const existingValueIndex = item.value.findIndex(
            (v) => v.language === languageCode
          );

          const newValue = [...item.value];
          if (existingValueIndex >= 0) {
            newValue[existingValueIndex] = { language: languageCode, value };
          } else {
            newValue.push({ language: languageCode, value });
          }

          return { ...item, value: newValue };
        }
        return item;
      })
    );
  };

  const updateItem = (
    itemKey: string,
    updates: Partial<MultiLanguageListItem>
  ) => {
    onChange(
      items.map((item) =>
        item.key === itemKey ? { ...item, ...updates } : item
      )
    );
  };

  const content = (
    <>
      <div className="mb-4">
        <Label value={t(languageDropdownLabelKey)} className="mb-2" />
        <LanguageDropdown
          selectedLanguage={selectedLanguage}
          onSelectLanguage={onLanguageChange}
          isDarkMode={isDarkMode}
        />
      </div>

      {items.map((item, index) => (
        <div key={item.key} className="relative mb-6">
          <div className="mb-2 flex items-center justify-between">
            <Label htmlFor={`item-${item.key}-input`}>
              {`${t(itemLabelKey)} ${index + 1} (${selectedLanguage.label})`}
              {required && <span className="ms-1 text-red-500">*</span>}
            </Label>
          </div>
          <div className="flex items-center gap-3">
            <TextInput
              id={`item-${item.key}-input`}
              className="w-3/5 p-0"
              type="text"
              value={getFieldValue(
                item.value || [],
                selectedLanguage.languageCode
              )}
              onChange={(e) =>
                updateItemValue(
                  item.key,
                  selectedLanguage.languageCode,
                  e.target.value
                )
              }
              placeholder={`${t(placeholderKey)}${t(`languages.${selectedLanguage.languageCode}`)}`}
            />
            {renderExtraContent &&
              renderExtraContent(item, (updates) =>
                updateItem(item.key, updates)
              )}
            {items.length > minItems && (
              <Button
                size="sm"
                color="failure"
                onClick={() => removeItem(item.key)}
                className="h-8 w-8 rounded-full"
              >
                <HiOutlineTrash className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      <Button
        onClick={addItem}
        color="success"
        className="mt-4"
        disabled={items.length >= maxItems}
      >
        <HiOutlinePlus className="me-2 h-4 w-4" />
        {t(addButtonTextKey)}
      </Button>
    </>
  );

  if (wrapInCard) {
    return <Card>{content}</Card>;
  }

  return <>{content}</>;
};
